import debounce from 'lodash/debounce';
import queryString from 'query-string';

import { getCashBackUserId } from './cash-back-user-id';
import instrumentedFetch from './instrumented-fetch';
import {
  getXGatewayServedBy,
  storeXGatewayServedBy,
} from './x-gateway-servedby';

import type { BackendGateway as BackendGatewayType } from '@shared/types/frontend/backend-gateway';
import type {
  EventSource,
  CallBack,
  CloseEventReference,
} from '@shared/types/frontend/event-source';
import type { HotelsSearchResponse } from '@shared/types/frontend/hotels-search-response';
import type { I18nService } from '@shared/types/i18n';
import type { UserContext, UserPreferences } from '@shared/types/user-context';
import type { JsonObject, Stay, Nullable } from '@shared/types/utils';
import type { EventSourcePolyfillInit } from 'event-source-polyfill';
import type { RequestInitWithRetry } from 'fetch-retry';

import { X_GATEWAY_SERVED_BY } from '@shared/constants/x-gateway-servedby';
import { DATE_FORMAT } from '@shared/utils-universal/i18n/date-formats';
import { StandardURL } from '@shared/utils-universal/url/StandardURL';

const getQueryParamStr = (obj: JsonObject) => queryString.stringify(obj);

export const DEBOUNCE_DELAY_MS = 500;

type QueryParams = {
  [key: string]: string | number | boolean | null | undefined | UserPreferences;
};

type Props = {
  backendUrl: string;
  eventSource: EventSource;
  i18n: I18nService;
  userContext: UserContext;
};

const BackendGateway = ({
  backendUrl,
  eventSource,
  i18n,
  userContext,
}: Props): BackendGatewayType => {
  let xGatewayServedBy = getXGatewayServedBy();

  // Track active EventSource cancel reference for this instance
  let activeEventSourceCancel: CloseEventReference | null = null;

  // Create debounced EventSource starter for this instance
  const debouncedEventSourceStart = debounce(
    (
      url: string,
      eventName: string,
      callback: CallBack<HotelsSearchResponse>,
      opts: EventSourcePolyfillInit,
    ) => {
      const cancelRef = eventSource.start(url, eventName, callback, opts);
      activeEventSourceCancel = cancelRef; // Store the active cancel reference
      return cancelRef;
    },
    DEBOUNCE_DELAY_MS,
    {
      leading: true, // Execute immediately on first call
      trailing: true, // Also execute after delay if more calls came
      maxWait: DEBOUNCE_DELAY_MS * 2, // Ensure eventual execution even with constant activity
    },
  );

  // Wrapper that returns consistent cancel function with proper typing
  const createDebouncedEventSource = (
    url: string,
    eventName: string,
    callback: CallBack<HotelsSearchResponse>,
    opts: EventSourcePolyfillInit,
  ): CloseEventReference => {
    debouncedEventSourceStart(url, eventName, callback, opts);

    // Always return a function that cancels the currently active EventSource
    return () => {
      if (activeEventSourceCancel) {
        activeEventSourceCancel();
        activeEventSourceCancel = null;
      }
    };
  };

  // Function to start EventSource based on configuration flag
  const startEventSource = (
    url: string,
    eventName: string,
    callback: CallBack<HotelsSearchResponse>,
    opts: EventSourcePolyfillInit,
  ): CloseEventReference => {
    if (userContext.enableDebouncedEventSource) {
      return createDebouncedEventSource(url, eventName, callback, opts);
    }
    return eventSource.start(url, eventName, callback, opts);
  };

  const getCommonOpts = (options: RequestInitWithRetry<typeof fetch> = {}) => {
    if (!xGatewayServedBy) {
      return options;
    }

    const { headers = {}, ...rest } = options;

    return {
      headers: { ...headers, [X_GATEWAY_SERVED_BY]: xGatewayServedBy },
      ...rest,
    };
  };

  const builtInFetch = async (
    url: string,
    options?: RequestInitWithRetry<typeof fetch>,
  ) => {
    const res = await instrumentedFetch(url, getCommonOpts(options));
    xGatewayServedBy = res.headers.get(X_GATEWAY_SERVED_BY);
    storeXGatewayServedBy(xGatewayServedBy);
    return res;
  };

  const getBackendUrl = (path: string, queryParams: QueryParams = {}) => {
    const { userPreferences, ...restParams } = queryParams;
    if (userPreferences) {
      Object.assign(userContext.userPreferences, userPreferences);
    }

    const params = userContext
      ? {
          ...restParams,
          user_context: JSON.stringify(userContext),
        }
      : restParams;

    return new StandardURL(
      `${backendUrl}${path}?${getQueryParamStr(params as JsonObject)}`,
    ).href;
  };

  const getCommonUrlParams = (stay: Stay) => {
    const { culture, formatDate } = i18n;
    const { childrenAges } = stay;

    return {
      market: culture.market,
      currency: culture.currency,
      locale: culture.locale,
      checkin: formatDate(stay.checkIn, DATE_FORMAT.NON_LOCALISED_SHORT),
      checkout: formatDate(stay.checkOut, DATE_FORMAT.NON_LOCALISED_SHORT),
      adults: stay.numberOfAdults,
      rooms: stay.numberOfRooms,
      children_ages:
        childrenAges && childrenAges.length
          ? childrenAges.join(',')
          : undefined,
    };
  };

  const getSearchUrlParams = (
    entityId: string,
    stay: Stay,
    offset: number,
    count: number,
    skyscannerNodeCode?: Nullable<string>,
  ) => ({
    ...getCommonUrlParams(stay),
    entity_id: entityId,
    skyscanner_node_code: skyscannerNodeCode,
    offset,
    count,
  });

  const getSearchUrl = (params: QueryParams) =>
    getBackendUrl('/search/v2', params);

  const getStaticSearchUrl = (params: JsonObject) =>
    getBackendUrl('/search/v2/static', params);

  return {
    startSearchUpdates: (
      {
        audienceId,
        bounds = {},
        chatSessionId,
        count,
        entityId,
        extraTraceInfo,
        filters,
        hpaVerification,
        offset,
        overrideHotelSortScore,
        priceType,
        searchTimeOfDay,
        skyscannerNodeCode,
        sort,
        stay,
        traceInfo,
        upsortHotels,
        userPreferences,
        ...rest
      },
      cb,
      enableCache,
    ) => {
      const params = getSearchUrlParams(
        entityId,
        stay,
        offset,
        count,
        skyscannerNodeCode,
      );
      const searchParams = {
        ...rest,
        ...params,
        sort,
        filters: JSON.stringify(filters),
        price_type: priceType,
        sw_lat: bounds?.south,
        sw_lng: bounds?.west,
        ne_lat: bounds?.north,
        ne_lng: bounds?.east,
        upsort_hotels: upsortHotels?.split(',')[0],
        trace_info: traceInfo,
        audience_id: audienceId,
        hpa_verification: hpaVerification,
        extra_trace_info: extraTraceInfo,
        from_cash_back: !!getCashBackUserId(),
        override_hotel_sort_score: overrideHotelSortScore,
        search_time_of_day: searchTimeOfDay,
        debug_info: userContext.showDebugInfo,
        chat_session_id: chatSessionId,
      };

      // Re-read the latest X-Gateway-Servedby value from cookie before starting search.
      // This is necessary because:
      // 1. BackendGateway is created early in the app lifecycle (line 49) and caches the cookie value in closure
      // 2. SearchPage set a new region-based gateway value in useEffect after BackendGateway creation
      // 3. Previous API responses may have triggered a fallback to a different gateway
      xGatewayServedBy = getXGatewayServedBy();

      if (enableCache) {
        const url = getStaticSearchUrl(searchParams);
        return startEventSource(
          url,
          'staticSearchState',
          cb,
          getCommonOpts() as EventSourcePolyfillInit,
        );
      }
      const url = getSearchUrl({ ...searchParams, userPreferences });
      return startEventSource(
        url,
        'searchState',
        cb,
        getCommonOpts() as EventSourcePolyfillInit,
      );
    },

    startMapUpdates: (
      {
        bounds,
        count = userContext.hotelCardListCount || 35,
        entityId,
        filters,
        overrideHotelSortScore,
        priceType,
        skyscannerNodeCode,
        stay,
        ...rest
      },
      cb,
    ) => {
      const offset = 0;
      const { east, north, south, west } = bounds || {};

      const params = getSearchUrlParams(
        entityId,
        stay,
        offset,
        count,
        skyscannerNodeCode,
      );

      const mapParams = {
        ...params,
        ...rest,
        sw_lat: south,
        sw_lng: west,
        ne_lat: north,
        ne_lng: east,
        filters: JSON.stringify(filters),
        from_cash_back: !!getCashBackUserId(),
        price_type: priceType,
        override_hotel_sort_score: overrideHotelSortScore,
      };

      const url = getBackendUrl(`/search/v2`, mapParams);
      return startEventSource(
        url,
        'searchState',
        cb,
        getCommonOpts() as EventSourcePolyfillInit,
      );
    },

    nearbyPoiResultV2: async ({
      entityId,
      lat,
      lon,
      maxDistance,
      minDistance,
      poiNumber,
    }) => {
      const url = getBackendUrl('/pois/v2/nearby', {
        lat,
        lon,
        minDistance,
        maxDistance,
        poiNumber,
        entityId,
      });
      const res = await builtInFetch(url);
      return res.json();
    },

    nearbyTransport: async ({ cityId, lat, limit, lon }) => {
      const url = getBackendUrl('/nearby-transport/v1', {
        lat,
        lon,
        limit,
        cityId,
      });
      const res = await builtInFetch(url);
      return res.json();
    },

    realReviews: async ({ hotelId, ...params }) => {
      const url = getBackendUrl(`/reviews/v4/${hotelId}`, {
        ...params,
      });
      const res = await builtInFetch(url);
      return res.json();
    },

    translateReview: async ({ partnerId, partnerReviewId, sourceLocale }) => {
      const url = getBackendUrl(
        `/reviews/v4/translate/${partnerReviewId}/${partnerId}/${sourceLocale}/${i18n.culture.locale}`,
        {
          market: i18n.culture.market,
          currency: i18n.culture.currency,
        },
      );
      const res = await builtInFetch(url);
      return res.json();
    },

    reviewHelpful: async ({
      hotelId,
      isHelpful,
      locale,
      partner,
      partnerReviewId,
    }) => {
      const url = getBackendUrl('/reviews/v1/useful');
      const res = await builtInFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          partner,
          locale,
          hotel_id: hotelId,
          partner_review_id: partnerReviewId,
          operation_type: isHelpful ? 1 : 0,
        }),
      });
      return res.json();
    },

    policyTranslation: async ({ ...params }) => {
      const url = getBackendUrl(`/v2/policy/translate`, {
        ...params,
        targetLocale: i18n.culture.locale,
      });
      const res = await builtInFetch(url);
      return res.json();
    },

    getHotelStatic: async ({ hotelId, type = 'static' }) => {
      const url = getBackendUrl(
        `/hotel-static/v1/${i18n.culture.locale}/${i18n.culture.market}/${i18n.culture.currency}/${hotelId}`,
        { type },
      );
      const res = await builtInFetch(url);
      return res.json();
    },

    poisEntity: async ({ entityIds }) => {
      const url = getBackendUrl('/pois/v1/entity', {
        entities: entityIds,
        locale: i18n.culture.locale,
      });
      const response = await builtInFetch(url);
      return response.json();
    },

    dealsResult: async () => {
      const { culture } = i18n;
      const params = {
        market: culture.market,
        currency: culture.currency,
        locale: culture.locale,
      };
      const url = getBackendUrl('/hotels-deals/v1', params);
      const response = await builtInFetch(url);
      return response.json();
    },
  };
};

export default BackendGateway;
